/**
 * CameraControls - Controles de UI para la cámara: modo POV, seguimiento de cuerpos
 * (Sol/planetas), distancia de seguimiento y actualización de la UI de tracking.
 *
 * Extraído de UIController para reducir su tamaño y agrupar una responsabilidad cohesionada.
 * Solo depende del renderizador 3D (que delega en su CameraController) y del DOM.
 */
export class CameraControls {
    /**
     * @param {Object} [renderer3D=null] - El renderizador 3D
     */
    constructor(renderer3D = null) {
        this.renderer3D = renderer3D;
    }

    /**
     * Asigna o actualiza la referencia al renderizador 3D.
     * @param {Object} renderer3D - El renderizador 3D
     */
    setRenderer3D(renderer3D) {
        this.renderer3D = renderer3D;
    }

    /**
     * Toggle POV (point of view) mode
     * @param {boolean} enabled - Whether POV mode should be enabled
     */
    togglePOVMode(enabled) {
        if (!this.renderer3D) return;

        const cameraController = this.renderer3D.getCameraController();
        if (cameraController && typeof cameraController.setPOVMode === 'function') {
            cameraController.setPOVMode(enabled);

            // Update UI to reflect the change
            const povToggle = document.getElementById('pov-mode-toggle');
            if (povToggle) {
                povToggle.checked = enabled;
            }

            console.log(`POV mode ${enabled ? 'enabled' : 'disabled'}`);
        } else {
            console.warn('POV mode not supported by current camera controller');

            // Reset toggle if not supported
            const povToggle = document.getElementById('pov-mode-toggle');
            if (povToggle) {
                povToggle.checked = false;
            }
        }
    }

    /**
     * Toggle camera tracking on/off
     * @param {boolean} enabled - Whether tracking should be enabled
     */
    toggleCameraTracking(enabled) {
        if (!this.renderer3D) return;

        if (enabled) {
            // If no object is currently being tracked, start tracking the sun
            const isTracking =
                this.renderer3D && typeof this.renderer3D.isCameraTracking === 'function'
                    ? this.renderer3D.isCameraTracking()
                    : false;
            if (!isTracking) {
                this.trackSun();
            }
        } else {
            if (this.renderer3D && typeof this.renderer3D.stopCameraTracking === 'function') {
                this.renderer3D.stopCameraTracking();
            }
            this.updateTrackingUI();
        }

        console.log(`Camera tracking ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Start tracking the sun
     */
    trackSun() {
        if (!this.renderer3D) return;

        if (typeof this.renderer3D.startCameraTracking === 'function') {
            this.renderer3D.startCameraTracking('sun');
        }
        this.updateTrackingUI();
        console.log('Started tracking sun');
    }

    /**
     * Start tracking a specific planet
     * @param {string} planetId - ID of the planet to track
     */
    trackPlanet(planetId) {
        if (!this.renderer3D) return;

        // Enable tracking if it's disabled
        const trackingToggle = document.getElementById('tracking-toggle');
        if (trackingToggle && !trackingToggle.checked) {
            trackingToggle.checked = true;
        }

        if (typeof this.renderer3D.startCameraTracking === 'function') {
            this.renderer3D.startCameraTracking(planetId);
        }
        this.updateTrackingUI();
        console.log(`Started tracking planet: ${planetId}`);
    }

    /**
     * Update tracking UI elements
     */
    updateTrackingUI() {
        const trackingToggle = document.getElementById('tracking-toggle');
        const trackingTarget = document.getElementById('tracking-target');
        const trackSunBtn = document.getElementById('track-sun-btn');
        const planetButtons = document.querySelectorAll('.planet-btn');
        const distanceSlider = document.getElementById('tracking-distance-slider');
        const distanceDisplay = document.getElementById('distance-display');

        if (!this.renderer3D) return;

        const isTracking =
            this.renderer3D && typeof this.renderer3D.isCameraTracking === 'function'
                ? this.renderer3D.isCameraTracking()
                : false;
        const trackedObjectId =
            this.renderer3D && typeof this.renderer3D.getTrackedObjectId === 'function'
                ? this.renderer3D.getTrackedObjectId()
                : null;

        // Update toggle state
        if (trackingToggle) {
            trackingToggle.checked = isTracking;
        }

        // Update tracking target display
        if (trackingTarget) {
            if (isTracking && trackedObjectId) {
                const targetName =
                    trackedObjectId === 'sun'
                        ? 'Sol'
                        : this.getPlanetName(trackedObjectId) || trackedObjectId;
                trackingTarget.textContent = targetName;
            } else {
                trackingTarget.textContent = 'Ninguno';
            }
        }

        // Update sun button state
        if (trackSunBtn) {
            if (trackedObjectId === 'sun') {
                trackSunBtn.classList.add('active');
            } else {
                trackSunBtn.classList.remove('active');
            }
        }

        // Update planet button states
        planetButtons.forEach((button) => {
            const planetId = button.getAttribute('data-planet');
            if (trackedObjectId === planetId) {
                button.classList.add('active');
            } else {
                button.classList.remove('active');
            }
        });

        // Update distance slider with current tracking distance
        if (distanceSlider && distanceDisplay && isTracking) {
            const currentDistance =
                this.renderer3D && typeof this.renderer3D.getTrackingDistance === 'function'
                    ? this.renderer3D.getTrackingDistance()
                    : 1800;

            distanceSlider.value = currentDistance;
            distanceDisplay.textContent = currentDistance.toString();
        }
    }

    /**
     * Set tracking distance for camera
     * @param {number} distance - New tracking distance
     */
    setTrackingDistance(distance) {
        if (!this.renderer3D) return;

        if (typeof this.renderer3D.setTrackingDistance === 'function') {
            this.renderer3D.setTrackingDistance(distance);
        }

        // Update distance display
        const distanceDisplay = document.getElementById('distance-display');
        if (distanceDisplay) {
            distanceDisplay.textContent = distance.toString();
        }

        console.log(`Tracking distance set to: ${distance}`);
    }

    /**
     * Get planet display name from ID
     * @param {string} planetId - Planet ID
     * @returns {string} Planet display name
     */
    getPlanetName(planetId) {
        const planetNames = {
            mercury: 'Mercurio',
            venus: 'Venus',
            earth: 'Tierra',
            mars: 'Marte',
            jupiter: 'Júpiter',
            saturn: 'Saturno',
            uranus: 'Urano',
            neptune: 'Neptuno',
        };
        return planetNames[planetId] || planetId;
    }
}
